import React, { useEffect, useState } from "react";

import { Modal } from "antd";

import HeaderTable from "../HeaderTable/HeaderTable";

import { useNotification } from "../../Toast/Notification";
import promoCodeServiceInstance, { PromoCode } from "../../../APIs/promoCode";
import PromoCodeTable from "./promoCodeTable/PromoCodeTable";

import { DiscountModalDashBoard } from "../../Modal/DiscountModalDashboard/DiscountModalDashboard";
import { usePagination } from "../../../hooks/use-pagination";
import useSearchItem from "../../../hooks/use-search-item";


const PromoCodee: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 8;

  const [id, setId] = useState<number | undefined>(undefined);
  const [code, setCode] = useState<string>("");
  const [codeType, setCodeType] = useState<string>("percentage");
  const [codeValue, setCodeValue] = useState<any>();
  const [isUsed, setIsUsed] = useState<boolean>(false);
  const [counter, setCounter] = useState<string>("");
  const [max_count, setMaxCount] = useState<string>("");
  const [usedCount, setIsusedCount] = useState<string>("");
  const [expiredDate, setExpiredDate] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(
    null
  );

  const [searchTerm, setSearchTerm] = useState("");

  const [filteredFilteredPromoCodes, setFilteredPromoCodes] = useState<
    PromoCode[]
  >([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const { notify } = useNotification();

  const [isUpdating, setIsUpdating] = useState(false);

  const handleButtonClick = () => {
    setIsModalVisible(true);
    setIsUpdating(false);
  };

  const handleModalCancel = () => {
    setSelectedPromoCode(null);
    setId(undefined);
    setCode("");
    setCodeType("");
    setCodeValue("0.00");
    setIsUsed(false);
    setMaxCount("");
    setIsusedCount("");
    setCounter("");
    setExpiredDate(null);

    setIsUpdating(false);
    setIsModalVisible(false);
  };

  const generatePromoCode = (length = 10): string => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let promoCode = "";
    for (let i = 0; i < length; i++) {
      promoCode += chars[Math.floor(Math.random() * chars.length)];
    }
    return promoCode;
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);

    const promoCodeData: PromoCode = {
      id,
      code: generatePromoCode(),
      code_type: codeType,
      code_value: codeValue,
      is_used: isUsed,
      used_count: usedCount,
      max_count: max_count,
      expired_date: expiredDate ? expiredDate : null,
    };

    try {
      const response = await promoCodeServiceInstance.checkPromoCodeExists(
        promoCodeData.code
      );

      if (response.data) {
        notify("Promo code already exists", "error");
        setIsLoading(false);
        return;
      }

      const { data, error } = await promoCodeServiceInstance.createPromoCode(
        promoCodeData
      );

      if (data) {
        notify("Promo Code created successfully", "success");

        fetchPromoCodeData();

        handleModalCancel();
        setSelectedPromoCode(null);

        setId(undefined);
        setCode("");
        setCodeType("percentage");
        setCodeValue("0.00");
        setIsUsed(false);

        setCounter("");
        setExpiredDate(null);
      } else {
        console.error("Error creating promo code:", error);
        notify("Error creating promo code", "error");
      }
    } catch (error) {
      console.error("Error creating promo code:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPromoCode = (promoCode: PromoCode) => {
    if (promoCode) {
      setSelectedPromoCode(promoCode);
      setId(promoCode.id || undefined);
      setCode(promoCode.code || "");
      setCodeType(promoCode.code_type || "");
      setCodeValue(promoCode.code_value || "0.00");
      setIsUsed(promoCode.is_used || false);
      setMaxCount(promoCode.max_count);
      setIsusedCount(promoCode.used_count);
      setExpiredDate(promoCode?.expired_date ? promoCode.expired_date : null);

      setIsUpdating(true);
      setIsModalVisible(true);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const promoCodeId = selectedPromoCode?.id;

      if (promoCodeId) {
        const updatedPromoCodeData: PromoCode = {
          id: promoCodeId,
          code,
          code_type: codeType,
          code_value: codeValue,
          is_used: isUsed,
          max_count: max_count,
          used_count: usedCount,
          expired_date: expiredDate ? expiredDate : null,
        };

        const { data, error } = await promoCodeServiceInstance.updatePromoCode(
          promoCodeId,
          updatedPromoCodeData
        );

        if (data) {
          fetchPromoCodeData();
          handleModalCancel();

          setSelectedPromoCode(null);
          setTimeout(() => {}, 3000);

          notify("Promo Code successfully updated", "success");
        } else {
          console.error("Error updating promo code:", error);
          notify("Error updating promo code", "error");
        }
      } else {
        console.error("Selected promo code or promo code ID is undefined");
      }
    } catch (error) {
      console.error("Error updating promo code:", error);
    } finally {
    }
  };

  const fetchPromoCodeData = async () => {
    try {
      const { data } = await promoCodeServiceInstance.getAllPromoCodes();
      if (data) {
        const sortedPromoCodes = data
          .filter((promo: PromoCode) => typeof promo.created_at === "string") // Runtime check
          .sort((a: PromoCode, b: PromoCode) => {
            const dateA = new Date(
              a.created_at ?? "1970-01-01T00:00:00Z"
            ).getTime();
            const dateB = new Date(
              b.created_at ?? "1970-01-01T00:00:00Z"
            ).getTime();
            return dateB - dateA;
          });

        setPromoCodes(sortedPromoCodes);
        setFilteredPromoCodes(sortedPromoCodes);
      }
    } catch (error) {
      console.error("Error fetching promo codes:", error);
    }
  };

  useEffect(() => {
    fetchPromoCodeData();
  }, []);

  const handleDeletePromoCode = async (promoCodeId: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this promo code?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        if (promoCodeId) {
          try {
            const { data, error } =
              await promoCodeServiceInstance.deletePromoCode(promoCodeId);

            if (data) {
              setPromoCodes((prevPromoCodes) =>
                prevPromoCodes.filter(
                  (promoCode) => promoCode.id !== promoCodeId
                )
              );

              notify("Promo Code deleted successfully", "success");

              fetchPromoCodeData();

              handleModalCancel();
              setIsModalVisible(false);
              setSelectedPromoCode(null);
              setTimeout(() => {}, 3000);
            } else {
              console.error("Error deleting promo code:", error);
              notify("Error deleting promo code", "error");
            }
          } catch (error) {
            console.error("Error deleting promo code:", error);
          } finally {
          }
        }
      },
    });
  };

  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const filterProductsFromSearch = useSearchItem({
    items: promoCodes,
    searchTerm,
    selectedRole,
    searchFields: ["code", "code_type", "code_value"],
  });

  useEffect(() => {
    if (searchTerm.length > 0) {
    } else {
      setFilteredPromoCodes(promoCodes);  
    }
  }, [promoCodes, filterProductsFromSearch, searchTerm]);

  const totalPages = Math.ceil(
    filteredFilteredPromoCodes.length / productsPerPage
  );
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredFilteredPromoCodes.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const pageNumbers = usePagination(totalPages);

  const handleSearchTermChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  const handleRoleChange = (event: any) => {
    setSelectedRole(event.target.value);
  };

  const handleOpenDiscountModal = () => {
    setIsModalVisible(true);
  };

  return (
    <div className="p-6">
      <div className="hidden sm:block  py-2">
        <div className="text-center py-2">
          <p
            className="mb-[10px] box-border font-semibold 
            text-[rgb(0,34,56)] block text-[35px] text-left h-auto
            leading-[38.4px]">
            Promo Code
          </p>
        </div>
        <div className="bg-white p-4 rounded-md w-5/4 mx-auto">
          <HeaderTable
            subtitle="Manage Promo Codes"
            buttonText="Add New Promo Code"
            inputPlaceholder="Search Promo Code"
            selectPlaceholder="Filter By Code Type"
            loading={false}
            searchTerm={searchTerm}
            selectedValue={selectedRole}
            onButtonClick={handleButtonClick}
            onSearchChange={handleSearchTermChange}
            onSelectChange={handleRoleChange}
            selectOptions={[
              { value: "percentage", label: "Percentage" },
              { value: "decimal", label: "Decimal" },
            ]}
          />
          <PromoCodeTable
            promoCodes={filteredFilteredPromoCodes}
            loading={isLoading}
            onEditPromoCode={handleEditPromoCode}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            pageNumbers={pageNumbers}
            handleDeletePromoCode={handleDeletePromoCode}
            currentProducts={currentProducts}
          />
        </div>
      </div>

      <DiscountModalDashBoard
        isDiscountModal={isModalVisible}
        promo_code={code}
        setPromoCode={setCode}
        handleSubmitCode={handleFormSubmit}
        handleOpenDiscountModal={handleOpenDiscountModal}
        onClose={handleModalCancel}
        isProcessing={isLoading}
        code_type={codeType}
        code_value={codeValue}
        counter={counter}
        isUpdating={isUpdating}
        expired_date={expiredDate}
        code={code}
        handleSaveEdit={handleSaveEdit}
        setCodeType={setCodeType}
        setCodeValue={setCodeValue}
        setCounter={setCounter}
        setExpiredDate={setExpiredDate}
        max_count={max_count}
        used_count={usedCount}
        setIsusedCount={setIsusedCount}
        setMaxCount={setMaxCount}
      />
    </div>
  );
};

export default PromoCodee;
